/**
 * Move a book's contribution deadline.
 *
 * EVERY TIME IN THIS DIALOG IS THE COUPLE'S. The field pre-fills with the
 * current deadline rendered in `couple_timezone`, the caption says which zone
 * that is, and the raw 'YYYY-MM-DDTHH:mm' string is sent to the server
 * unconverted — the server reads couple_timezone off the book row and does the
 * conversion in Postgres. An operator in London must not be able to move a
 * Denver couple's midnight by eight hours just by looking at it.
 *
 * `@mui/x-date-pickers` is NOT a dependency of this app, and adding one for a
 * single field would be the wrong trade — a native <input type="datetime-local">
 * is exactly the right control here and needs no timezone-aware picker,
 * precisely because the zone is fixed by the book.
 *
 * Confirm-gate: no network call until Save is pressed. The past-date check is a
 * client-side courtesy; the server re-validates the same rule in SQL and
 * answers 422 DEADLINE_IN_PAST.
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Alert,
  Typography,
  CircularProgress,
} from '@mui/material';
import { dateTimeInZone, toDatetimeLocalInZone, isLocalDateTimeInPast } from '../weddingFormat';

interface Props {
  open: boolean;
  weddingCode: string;
  currentDeadline: string | null;
  coupleTimezone: string;
  submitting?: boolean;
  error?: string | null;
  onClose: () => void;
  onConfirm: (deadlineLocal: string) => void;
}

const ExtendDeadlineDialog: React.FC<Props> = ({
  open,
  weddingCode,
  currentDeadline,
  coupleTimezone,
  submitting = false,
  error,
  onClose,
  onConfirm,
}) => {
  const [value, setValue] = React.useState('');

  React.useEffect(() => {
    if (open) setValue(toDatetimeLocalInZone(currentDeadline, coupleTimezone));
  }, [open, currentDeadline, coupleTimezone]);

  const inPast = value ? isLocalDateTimeInPast(value, coupleTimezone) : false;
  const canConfirm = !submitting && !!value && !inPast;

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Move the deadline for {weddingCode}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Current deadline: <strong>{dateTimeInZone(currentDeadline, coupleTimezone)}</strong>
          </Typography>

          <TextField
            label="New deadline"
            type="datetime-local"
            fullWidth
            required
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={submitting}
            slotProps={{ inputLabel: { shrink: true } }}
            error={inPast}
            helperText={
              inPast
                ? 'That moment has already passed in the couple’s timezone.'
                : `All times are in the couple’s timezone: ${coupleTimezone}`
            }
          />

          <Alert severity="info">
            Moving the deadline reopens contributions if the book had closed on it, and the
            couple sees “the chapter deadline moved” in their activity feed.
          </Alert>

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!canConfirm}
          onClick={() => onConfirm(value)}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Save deadline
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExtendDeadlineDialog;
